import React, { useState, useEffect } from 'react';
import Navigation from '../Navigation/navigation';
import { useAuth } from '../Auth/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Pie, Bar, Doughnut } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const Dashboard = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedClinic, setSelectedClinic] = useState('');
  const [clinicOptions, setClinicOptions] = useState([]);

  const [dashboardData, setDashboardData] = useState({
    totalStats: {
      totalStaff: 0,
      activeWorkers: 0,
      totalClinics: 0
    },
    genderDistribution: {
      male: 0,
      female: 0,
      other: 0
    },
    benefitCoverage: {
      activeStaffTotal: 0,
      withRegularSalary: 0,
      withAccommodation: 0
    },
    staffByClinic: [],
    cadreByClinic: []
  });

  // Authentication checks
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/signin');
      return;
    }
    
    if (!user || (user.role !== 'admin' && user.role !== 'user')) {
      navigate('/signin');
      return;
    }
  }, [isAuthenticated, user, navigate]);

  const fetchDashboardData = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Modify API call to include user-specific filtering for user role
      let endpoint = '/api/dashboard/stats';
      if (user.role === 'user') {
        // For regular users, filter by their own data based on user_id
        endpoint += `?user=${user.user_id}`;
        if (selectedClinic) {
          endpoint += `&clinic=${selectedClinic}`;
        }
      } else {
        // For admin users, show all data with optional clinic filter
        if (selectedClinic) {
          endpoint += `?clinic=${selectedClinic}`;
        }
      }
      
      const response = await api.get(endpoint);
      
      if (response.data && response.data.success) {
        const dashboardStats = response.data.data;
        const totalStaff = parseInt(dashboardStats.totalStats?.totalStaff) || 0;
        const activeWorkers = parseInt(dashboardStats.totalStats?.activeWorkers) || 0;
        const inactiveWorkers = Math.max(totalStaff - activeWorkers, 0);

        // Keep dashboard totals consistent with current clinic filter
        dashboardStats.totalStats = {
          totalStaff: totalStaff,
          activeWorkers: activeWorkers,
          inactiveWorkers: inactiveWorkers,
          totalClinics: dashboardStats.totalStats?.totalClinics || 0
        };
        
        setDashboardData(dashboardStats);
        
        // Set clinic options from the data
        if (response.data.data.staffByClinic && response.data.data.staffByClinic.length > 0) {
          const clinics = response.data.data.staffByClinic.map(item => ({
            id: item.clinic_id,
            name: item.clinic_name
          }));
          setClinicOptions(clinics);
        }
      } else {
        setError('Failed to fetch dashboard data');
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError('Error fetching dashboard data: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated && user) {
      fetchDashboardData();
    }
  }, [isAuthenticated, user, selectedClinic]);

  const handleClinicChange = (e) => {
    setSelectedClinic(e.target.value);
  };

  // Gender distribution chart data
  const genderCounts = {
    male: dashboardData.genderDistribution?.male || 0,
    female: dashboardData.genderDistribution?.female || 0,
    other: dashboardData.genderDistribution?.other || 0
  };
  const genderTotal = genderCounts.male + genderCounts.female + genderCounts.other;
  const formatGenderLabel = (name, count) => {
    const percentage = genderTotal > 0 ? ((count / genderTotal) * 100).toFixed(1) : '0.0';
    return `${name} (${count}) (${percentage}%)`;
  };

  const genderChartData = {
    labels: [
      formatGenderLabel('Male', genderCounts.male),
      formatGenderLabel('Female', genderCounts.female),
      formatGenderLabel('Other', genderCounts.other)
    ],
    datasets: [
      {
        data: [
          genderCounts.male,
          genderCounts.female,
          genderCounts.other
        ],
        backgroundColor: [
          '#3B82F6',
          '#EF4444',
          '#10B981'
        ],
        borderWidth: 2,
        borderColor: '#fff'
      }
    ]
  };

  const genderChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          padding: 15,
          usePointStyle: true,
          pointStyle: 'circle',
          font: {
            size: 12
          }
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return context.label || '';
          }
        }
      }
    }
  };

  const activeBenefitBase = dashboardData.benefitCoverage?.activeStaffTotal || dashboardData.totalStats?.activeWorkers || 0;
  const withRegularSalary = dashboardData.benefitCoverage?.withRegularSalary || 0;
  const withoutRegularSalary = Math.max(activeBenefitBase - withRegularSalary, 0);
  const withAccommodation = dashboardData.benefitCoverage?.withAccommodation || 0;
  const withoutAccommodation = Math.max(activeBenefitBase - withAccommodation, 0);

  const formatBenefitLabel = (label, count) => {
    const pct = activeBenefitBase > 0 ? ((count / activeBenefitBase) * 100).toFixed(1) : '0.0';
    return `${label} (${count}) (${pct}%)`;
  };

  const salaryCoverageChartData = {
    labels: [
      formatBenefitLabel('Regular Salary', withRegularSalary),
      formatBenefitLabel('No Regular Salary', withoutRegularSalary)
    ],
    datasets: [
      {
        data: [withRegularSalary, withoutRegularSalary],
        backgroundColor: ['#16A34A', '#E5E7EB'],
        borderWidth: 1,
        borderColor: '#ffffff'
      }
    ]
  };

  const accommodationCoverageChartData = {
    labels: [
      formatBenefitLabel('With Accommodation', withAccommodation),
      formatBenefitLabel('No Accommodation', withoutAccommodation)
    ],
    datasets: [
      {
        data: [withAccommodation, withoutAccommodation],
        backgroundColor: ['#0EA5E9', '#E5E7EB'],
        borderWidth: 1,
        borderColor: '#ffffff'
      }
    ]
  };

  const benefitPieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 12,
          usePointStyle: true,
          pointStyle: 'circle',
          font: {
            size: 11
          }
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return context.label || '';
          }
        }
      }
    }
  };

  // Staff by clinic chart data
  const staffByClinicChartData = {
    labels: (dashboardData.staffByClinic || []).map(item => item.clinic_name || 'Unknown'),
    datasets: [
      {
        label: 'Active Staff',
        data: (dashboardData.staffByClinic || []).map(item => item.staff_count || 0),
        backgroundColor: '#3B82F6',
        borderColor: '#1E40AF',
        borderWidth: 1
      }
    ]
  };

  // Cadre by clinic chart data with consistent colors per cadre
  const generateAggregatedCadreData = () => {
    const data = dashboardData.cadreByClinic || [];
    
    // Group by cadre name and sum counts
    const cadreGroups = {};
    data.forEach(item => {
      const cadreName = item.cadre_name || 'Unknown';
      if (cadreGroups[cadreName]) {
        cadreGroups[cadreName] += item.cadre_count || 0;
      } else {
        cadreGroups[cadreName] = item.cadre_count || 0;
      }
    });

    return Object.entries(cadreGroups).map(([name, count]) => ({
      cadre_name: name,
      cadre_count: count
    }));
  };

  const generateCadreColors = () => {
    const aggregatedData = generateAggregatedCadreData();
    
   
    const colorPalette = [
      
      '#059669', // Emerald Green
      '#7C3AED', // Bright Purple
      '#DB2777', // Pink/Magenta
      '#EA580C', // Bright Orange
      '#0891B2', // Cyan Blue
      '#65A30D', // Lime Green
      '#F59E0B', // Amber/Yellow
      '#8B5CF6', // Violet
      '#06B6D4', // Sky Blue
      '#84CC16', // Yellow Green
      '#F97316', // Orange
      '#EC4899', // Hot Pink
      '#10B981', // Teal
      '#6366F1', // Indigo
      '#F43F5E', // Rose
      '#8B5A2B', // Brown
      '#1F2937', // Very Dark Gray
      '#4B5563', // Dark Gray
      '#9CA3AF', // Light Gray
    ];

    
    return aggregatedData.map((item, index) => {
      return colorPalette[index % colorPalette.length];
    });
  };

  const aggregatedCadreData = generateAggregatedCadreData();

  const cadreByClinicChartData = {
    labels: aggregatedCadreData.map(item => 
      `${item.cadre_name} (${item.cadre_count})`
    ),
    datasets: [
      {
        data: aggregatedCadreData.map(item => item.cadre_count),
        backgroundColor: generateCadreColors(),
        borderWidth: 1,
        borderColor: '#ffffff'
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          padding: 15,
          usePointStyle: true,
          pointStyle: 'circle',
          font: {
            size: 12
          },
          generateLabels: function(chart) {
            const data = chart.data;
            if (data.labels.length && data.datasets.length) {
              return data.labels.map((label, i) => {
                const meta = chart.getDatasetMeta(0);
                const style = meta.controller.getStyle(i);
                return {
                  text: label,
                  fillStyle: style.backgroundColor,
                  strokeStyle: style.borderColor,
                  lineWidth: style.borderWidth,
                  pointStyle: 'circle',
                  hidden: isNaN(data.datasets[0].data[i]) || meta.data[i].hidden,
                  index: i
                };
              });
            }
            return [];
          }
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.parsed || 0;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${label}: ${percentage}%`;
          }
        }
      }
    }
  };

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          precision: 0
        }
      }
    }
  };

  if (loading) {
    return (
      <div>
        <Navigation />
        <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navigation />
      
      <div className="min-h-screen bg-gray-50 pt-20 p-6">
        <div className="max-w-7xl mx-auto">
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
              {error}
            </div>
          )}

          {/* Filter and Cards in parallel - Only show for admin users */}
          {user.role === 'admin' && (
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-3 mb-8">
              {/* Clinic Filter */}
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 flex items-center">
                <select
                  value={selectedClinic}
                  onChange={handleClinicChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                >
                  <option value="">All Clinics</option>
                  {(clinicOptions || []).map((clinic) => (
                    <option key={clinic.id} value={clinic.id}>
                      {clinic.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Total Staff - Light Blue Card */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg shadow-sm p-4">
                <p className="text-xs font-medium text-blue-700 mb-1">Total Staff</p>
                <p className="text-2xl font-bold text-blue-800">{dashboardData.totalStats?.totalStaff || 0}</p>
              </div>
              
              {/* Active Staff - Light Green Card */}
              <div className="bg-green-50 border border-green-200 rounded-lg shadow-sm p-4">
                <p className="text-xs font-medium text-green-700 mb-1">Active Staff</p>
                <p className="text-2xl font-bold text-green-800">{dashboardData.totalStats?.activeWorkers || 0}</p>
              </div>

              {/* Inactive Staff - Light Red Card */}
              <div className="bg-red-50 border border-red-200 rounded-lg shadow-sm p-4">
                <p className="text-xs font-medium text-red-700 mb-1">Inactive Staff</p>
                <p className="text-2xl font-bold text-red-800">{dashboardData.totalStats?.inactiveWorkers || 0}</p>
              </div>
            </div>
          )}

          {/* Welcome message for regular users */}
          {user.role === 'user' && (
            <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-8 rounded-lg shadow-lg mb-8">
              <div className="text-center">
                <h2 className="text-2xl font-bold mb-2">Welcome to Your Dashboard</h2>
                <p className="text-blue-100">
                  Hello <strong>{user.user_id}</strong>! You have successfully logged into the HR Management System.
                </p>
                <p className="text-blue-100 mt-2">
                  Use the navigation menu above to access your available features and manage your information.
                </p>
              </div>
            </div>
          )}

          {/* Charts Section - Only show for admin users */}
          {user.role === 'admin' && (
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
              {/* Gender Distribution Chart */}
              <div className="bg-white p-4 rounded-lg shadow-lg">
                <h2 className="text-sm font-semibold text-gray-800 mb-3">
                  Active Staff by Gender
                </h2>
                <div className="h-64">
                  <Pie data={genderChartData} options={genderChartOptions} />
                </div>
              </div>

              {/* Cadre Distribution Chart */}
              <div className="bg-white p-4 rounded-lg shadow-lg">
                <h2 className="text-sm font-semibold text-gray-800 mb-3">
                  Active Staff by Cadre
                </h2>
                <div className="h-64">
                  <Doughnut data={cadreByClinicChartData} options={chartOptions} />
                </div>
              </div>

              {/* Staff by Clinic Chart */}
              <div className="bg-white p-4 rounded-lg shadow-lg">
                <h2 className="text-sm font-semibold text-gray-800 mb-3">
                  Active Staff by Clinic
                </h2>
                <div className="h-64">
                  <Bar data={staffByClinicChartData} options={barChartOptions} />
                </div>
              </div>

              {/* Benefits Coverage Chart */}
              <div className="bg-white p-4 rounded-lg shadow-lg">
                <h2 className="text-sm font-semibold text-gray-800 mb-3">
                  Active Staff Benefits Coverage
                </h2>
                <p className="text-xs text-gray-500 mb-2">
                  Base: {activeBenefitBase} active staff
                </p>
                <div className="grid grid-cols-1 gap-4">
                  <div className="h-28">
                    <Doughnut data={salaryCoverageChartData} options={benefitPieOptions} />
                  </div>
                  <div className="h-28">
                    <Doughnut data={accommodationCoverageChartData} options={benefitPieOptions} />
                  </div>
                </div>
              </div>

            </div>
          )}

          {/* Information message for regular users */}
          {user.role === 'user' && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
              <h3 className="text-lg font-medium text-blue-800 mb-2">Dashboard Information</h3>
              <p className="text-blue-600">
                This dashboard is simplified for user access. For detailed statistics, charts, and analytics, please contact your administrator.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;